import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { DEFAULT_SETTINGS, DEFAULT_SNAP, SHAPE_DEFS } from "../constants/defaults";
import { serializeBase, deserializeBase } from "../lib/loaders";

const CadContext = createContext(null);
export const useCad = () => useContext(CadContext);

let idc = 1;
const uid = (p) => `${p}-${idc++}-${Math.random().toString(36).slice(2, 6)}`;

function makeShape(type, count, level) {
  const def = SHAPE_DEFS[type];
  return {
    id: uid("shape"),
    name: `${def.label} ${count}`,
    type,
    params: { ...def.params },
    x: 0, y: 0, rotation: 0,
    level,
    thickness: DEFAULT_SETTINGS.thickness,
    edgeChamfer: DEFAULT_SETTINGS.edgeChamfer,
    edgeType: DEFAULT_SETTINGS.edgeType,
    united: false,
    visible: true,
    connectors: [],
  };
}

function seed() {
  const bottom = makeShape("rectangle", 1, 0);
  bottom.params = { w: 66, h: 44 };
  const top = makeShape("star", 2, 1);
  top.params = { points: 5, outerR: 26, innerR: 12 };
  top.connectors = [
    { id: uid("cn"), x: -10, y: 0, override: null },
    { id: uid("cn"), x: 10, y: 0, override: null },
  ];
  return [bottom, top];
}

export function CadProvider({ children }) {
  const [layers, setLayers] = useState(seed);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [tool, setTool] = useState("select"); // select | connector | draw | measure
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [snap, setSnap] = useState(DEFAULT_SNAP);
  const [xray, setXray] = useState(false);
  const [base, setBase] = useState(null);
  const [layout, setLayout] = useState("split");
  const [activeLevel, setActiveLevel] = useState(1);
  const shapeCount = useRef(2);

  // ---- undo/redo history (coalesced) ----
  const hist = useRef({ past: [], future: [], last: layers, t: Date.now(), lock: false });
  const [histTick, setHistTick] = useState(0);
  useEffect(() => {
    const h = hist.current;
    if (h.lock) { h.lock = false; h.last = layers; h.t = Date.now(); return; }
    if (layers === h.last) return;
    const now = Date.now();
    if (now - h.t < 500) { h.last = layers; h.t = now; return; }
    h.past.push(h.last);
    if (h.past.length > 80) h.past.shift();
    h.future = [];
    h.last = layers; h.t = now;
    setHistTick((v) => v + 1);
  }, [layers]);

  const undo = useCallback(() => {
    const h = hist.current;
    if (!h.past.length) return;
    h.future.push(h.last);
    const prev = h.past.pop();
    h.lock = true;
    setLayers(prev);
    setHistTick((v) => v + 1);
  }, []);
  const redo = useCallback(() => {
    const h = hist.current;
    if (!h.future.length) return;
    h.past.push(h.last);
    const nxt = h.future.pop();
    h.lock = true;
    setLayers(nxt);
    setHistTick((v) => v + 1);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // ---- shape actions ----
  const addShape = useCallback((type) => {
    shapeCount.current += 1;
    const s = makeShape(type, shapeCount.current, activeLevel);
    s.edgeChamfer = settings.edgeChamfer;
    s.edgeType = settings.edgeType;
    s.thickness = settings.thickness;
    setLayers((ls) => [...ls, s]);
    setSelectedId(s.id);
    setTool("select");
  }, [settings, activeLevel]);

  const addCustomPlate = useCallback((worldPoints) => {
    if (worldPoints.length < 3) return;
    const cxp = worldPoints.reduce((a, p) => a + p.x, 0) / worldPoints.length;
    const cyp = worldPoints.reduce((a, p) => a + p.y, 0) / worldPoints.length;
    shapeCount.current += 1;
    const s = {
      id: uid("shape"), name: `Custom ${shapeCount.current}`, type: "custom",
      params: { points: worldPoints.map((p) => ({ x: +(p.x - cxp).toFixed(3), y: +(p.y - cyp).toFixed(3) })) },
      x: +cxp.toFixed(2), y: +cyp.toFixed(2), rotation: 0, level: activeLevel,
      thickness: settings.thickness, edgeChamfer: settings.edgeChamfer, edgeType: settings.edgeType,
      united: false, visible: true, connectors: [],
    };
    setLayers((ls) => [...ls, s]);
    setSelectedId(s.id);
    setTool("select");
  }, [settings, activeLevel]);

  const addLevel = useCallback(() => {
    setActiveLevel((lv) => {
      const max = layers.length ? Math.max(...layers.map((s) => s.level)) : -1;
      return Math.max(max, lv) + 1;
    });
  }, [layers]);

  const selectShape = useCallback((id) => {
    setSelectedId(id);
    setSelectedConnector(null);
    const s = id && layers.find((l) => l.id === id);
    if (s) setActiveLevel(s.level);
  }, [layers]);

  const resetSettings = useCallback(() => setSettings(DEFAULT_SETTINGS), []);
  const setBaseTransform = useCallback((patch) => {
    setBase((b) => (b ? { ...b, transform: { ...(b.transform || { x: 0, y: 0, z: 0, rx: 0, ry: 0, rz: 0 }), ...patch } } : b));
  }, []);

  const serializeProject = useCallback(() => ({ layers, settings, snap, base: serializeBase(base) }), [layers, settings, snap, base]);
  const applyProject = useCallback((data) => {
    if (!data) return;
    setLayers(data.layers || []);
    setSettings({ ...DEFAULT_SETTINGS, ...(data.settings || {}) });
    setSnap({ ...DEFAULT_SNAP, ...(data.snap || {}) });
    setBase(data.base ? deserializeBase(data.base) : null);
    setSelectedId(null);
    setSelectedConnector(null);
    hist.current = { past: [], future: [], last: data.layers || [], t: Date.now(), lock: true };
  }, []);

  const updatePlate = useCallback((id, patch) => {
    setLayers((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }, []);
  const updateParams = useCallback((id, patch) => {
    setLayers((ls) => ls.map((l) => (l.id === id ? { ...l, params: { ...l.params, ...patch } } : l)));
  }, []);
  const deletePlate = useCallback((id) => {
    setLayers((ls) => ls.filter((l) => l.id !== id));
    setSelectedId((s) => (s === id ? null : s));
  }, []);
  const duplicatePlate = useCallback((id) => {
    setLayers((ls) => {
      const src = ls.find((l) => l.id === id);
      if (!src) return ls;
      const copy = { ...src, id: uid("shape"), name: `${src.name} copy`, x: src.x + 5, y: src.y - 5, connectors: src.connectors.map((c) => ({ ...c, id: uid("cn"), x: c.x + 5, y: c.y - 5 })) };
      const idx = ls.findIndex((l) => l.id === id);
      const next = [...ls]; next.splice(idx + 1, 0, copy); return next;
    });
  }, []);
  const moveLayer = useCallback((id, dir) => {
    setLayers((ls) => {
      const idx = ls.findIndex((l) => l.id === id);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= ls.length) return ls;
      const next = [...ls]; [next[idx], next[j]] = [next[j], next[idx]]; return next;
    });
  }, []);
  const moveShapeLevel = useCallback((id, dir) => {
    setLayers((ls) => ls.map((l) => (l.id === id ? { ...l, level: Math.max(0, (l.level || 0) + dir) } : l)));
  }, []);
  const toggleUnite = useCallback((id) => {
    setLayers((ls) => ls.map((l) => (l.id === id ? { ...l, united: !l.united } : l)));
  }, []);

  const addConnector = useCallback((plateId, x, y) => {
    const cn = { id: uid("cn"), x: +x.toFixed(2), y: +y.toFixed(2), override: null };
    setLayers((ls) => ls.map((l) => (l.id === plateId ? { ...l, connectors: [...l.connectors, cn] } : l)));
    setSelectedConnector(cn.id);
  }, []);
  const updateConnector = useCallback((plateId, cnId, patch) => {
    setLayers((ls) => ls.map((l) => (l.id === plateId ? { ...l, connectors: l.connectors.map((c) => (c.id === cnId ? { ...c, ...patch } : c)) } : l)));
  }, []);
  const deleteConnector = useCallback((plateId, cnId) => {
    setLayers((ls) => ls.map((l) => (l.id === plateId ? { ...l, connectors: l.connectors.filter((c) => c.id !== cnId) } : l)));
  }, []);

  const snapshot = useCallback(() => {
    const h = hist.current;
    h.past.push(h.last); if (h.past.length > 80) h.past.shift();
    h.future = []; h.t = 0;
  }, []);

  const value = {
    layers, setLayers,
    selectedId, setSelectedId: selectShape, setSelectedIdRaw: setSelectedId,
    selectedConnector, setSelectedConnector,
    tool, setTool,
    settings, setSettings,
    snap, setSnap,
    xray, setXray,
    base, setBase, setBaseTransform,
    layout, setLayout,
    activeLevel, setActiveLevel, addLevel,
    addShape, addCustomPlate, updatePlate, updateParams, deletePlate, duplicatePlate,
    moveLayer, moveShapeLevel, toggleUnite,
    addConnector, updateConnector, deleteConnector,
    resetSettings, serializeProject, applyProject,
    undo, redo, snapshot,
    canUndo: hist.current.past.length > 0, canRedo: hist.current.future.length > 0,
    selected: layers.find((l) => l.id === selectedId) || null,
  };
  void histTick;
  return <CadContext.Provider value={value}>{children}</CadContext.Provider>;
}
